import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import { WIDTH_MD, WIDTH_SM } from '@utils/constants';
import { getImgixSrc } from '@utils/images';

import { MIDDLE_SLIDE_SHOW_IMAGES } from './constant';

const AUTO_ADVANCE_MS = 5500;

function getSlidesPerView(width: number): number {
  if (width >= WIDTH_MD) {
    return 3;
  }
  if (width >= WIDTH_SM) {
    return 2;
  }
  return 1;
}

function chunkIntoPages(paths: readonly string[], perPage: number): string[][] {
  const pages: string[][] = [];
  for (let i = 0; i < paths.length; i += perPage) {
    pages.push(paths.slice(i, i + perPage));
  }
  return pages;
}

const GlanceSlideshow = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [activePage, setActivePage] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    const measure = () => setContainerWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mq.matches);
    const onChange = () => setPrefersReducedMotion(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const measured = containerWidth > 0;
  const slidesPerView = measured ? getSlidesPerView(containerWidth) : 1;

  const pages = useMemo(() => {
    if (!measured) {
      return [] as string[][];
    }
    return chunkIntoPages(MIDDLE_SLIDE_SHOW_IMAGES, slidesPerView);
  }, [measured, slidesPerView]);

  const pageCount = pages.length;

  useEffect(() => {
    setActivePage((p) => (pageCount === 0 ? 0 : Math.min(p, pageCount - 1)));
  }, [pageCount]);

  useEffect(() => {
    if (pageCount <= 1 || prefersReducedMotion) {
      return undefined;
    }
    const id = window.setInterval(() => {
      setActivePage((p) => (p + 1) % pageCount);
    }, AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [pageCount, prefersReducedMotion]);

  const nextPage = pageCount > 0 ? (activePage + 1) % pageCount : 0;

  return (
    <div
      className={clsx(
        'mx-auto w-full max-w-[1728px]',
        /* Edge-to-edge on small viewports (break out of page gutters). */
        'max-md:relative max-md:left-1/2 max-md:w-screen max-md:max-w-[100vw] max-md:-translate-x-1/2',
        'md:left-auto md:translate-x-0'
      )}
    >
      <div
        ref={containerRef}
        className="relative mb-112 h-[min(52vw,360px)] w-full overflow-hidden sm:h-[min(28vw,380px)] md:h-[min(22vw,420px)]"
        role="presentation"
        aria-roledescription="carousel"
        aria-label="Event photos"
      >
        {measured && pageCount > 0 ? (
          pages.map((pathsInPage, pageIndex) => (
            <div
              key={pageIndex}
              className={clsx(
                'absolute inset-0 flex gap-0 transition-opacity',
                prefersReducedMotion ? 'duration-0' : 'duration-500 ease-in-out',
                pageIndex === activePage ? 'z-[1] opacity-100' : 'pointer-events-none z-0 opacity-0'
              )}
              aria-hidden={pageIndex !== activePage}
            >
              {pathsInPage.map((path) => (
                <div key={path} className="h-full min-w-0 flex-1 self-stretch">
                  <LazyImgixImage
                    fit="max"
                    src={getImgixSrc({ path, isEnvDependent: false })}
                    alt=""
                    className="block h-full min-h-0 w-full min-w-0 object-cover object-center"
                    sizes="(max-width: 767px) 100vw, (max-width: 991px) 50vw, 33vw"
                    visibleByDefault={pageIndex === activePage || pageIndex === nextPage}
                    wrapperClassName="!block h-full min-h-0 min-w-0 w-full leading-[0]"
                  />
                </div>
              ))}
            </div>
          ))
        ) : (
          <div
            className="h-[min(52vw,360px)] w-full bg-grayscale-charcoal sm:h-[min(28vw,380px)] md:h-[min(22vw,420px)]"
            aria-hidden
          />
        )}
      </div>
    </div>
  );
};

export default GlanceSlideshow;
